import { useNavigate, useSearch } from "@tanstack/solid-router";
import { Show, createResource, createSignal, For } from "solid-js";

import {
  consumeReturnTo,
  exchangeSessionCode,
  type Tenant,
  type TokenExchangeResult,
} from "~/lib/auth";

type CallbackSearch = {
  session_code?: string;
  error?: string;
};

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as () => CallbackSearch;

  const [picking, setPicking] = createSignal(false);
  const [tenants, setTenants] = createSignal<Tenant[]>([]);
  const [pickError, setPickError] = createSignal<string | null>(null);

  const finalize = (
    _result: Extract<TokenExchangeResult, { requires_tenant_selection?: false }>,
  ) => {
    const returnTo = consumeReturnTo();
    void navigate({ to: returnTo });
  };

  const [result] = createResource(
    () => search().session_code,
    async (sessionCode): Promise<{ error?: string; ok?: boolean }> => {
      if (!sessionCode) return { error: "Missing session_code" };
      try {
        const r = await exchangeSessionCode(sessionCode);
        if (r.requires_tenant_selection) {
          setTenants(r.tenants);
          setPicking(true);
          return { ok: true };
        }
        finalize(r);
        return { ok: true };
      } catch (err) {
        return { error: (err as Error).message };
      }
    },
  );

  const handlePick = async (tenantId: string) => {
    const code = search().session_code;
    if (!code) return;
    setPickError(null);
    try {
      const r = await exchangeSessionCode(code, tenantId);
      if (r.requires_tenant_selection) {
        // Shouldn't happen — backend gave us the list, we picked one.
        setPickError("Unexpected response from server.");
        return;
      }
      finalize(r);
    } catch (err) {
      setPickError((err as Error).message);
    }
  };

  return (
    <div class="min-h-screen flex items-center justify-center bg-base-200">
      <div class="card w-96 bg-base-100 shadow-xl">
        <div class="card-body items-center text-center gap-4">
          <Show when={search().error}>
            <h1 class="card-title text-error">Login failed</h1>
            <p class="text-sm">{search().error}</p>
            <a class="btn btn-primary btn-sm" href="/login">
              Try again
            </a>
          </Show>

          <Show when={!search().error}>
            <Show
              when={!picking() && result()}
              fallback={
                <Show
                  when={picking()}
                  fallback={
                    <>
                      <span class="loading loading-spinner loading-lg" aria-hidden="true" />
                      <p class="text-sm opacity-70">Completing sign-in…</p>
                    </>
                  }
                >
                  <h1 class="card-title">Choose your organization</h1>
                  <p class="text-sm opacity-70">
                    Your account is in more than one organization. Pick which one to sign in to.
                  </p>
                  <ul class="w-full flex flex-col gap-2 mt-2" aria-label="Organizations">
                    <For each={tenants()}>
                      {(t) => (
                        <li>
                          <button
                            class="btn btn-outline w-full justify-start"
                            onClick={() => void handlePick(t.id)}
                          >
                            <span class="font-medium">{t.name}</span>
                            <span class="opacity-50 ml-2 text-xs">{t.slug}</span>
                          </button>
                        </li>
                      )}
                    </For>
                  </ul>
                  <Show when={pickError()}>
                    <div role="alert" class="alert alert-error text-sm mt-2">
                      {pickError()}
                    </div>
                  </Show>
                </Show>
              }
            >
              {(r) => (
                <Show when={r().error} fallback={<p class="text-sm">Redirecting…</p>}>
                  <h1 class="card-title text-error">Login failed</h1>
                  <p class="text-sm">{r().error}</p>
                  <a class="btn btn-primary btn-sm" href="/login">
                    Try again
                  </a>
                </Show>
              )}
            </Show>
          </Show>
        </div>
      </div>
    </div>
  );
}
